import styles from './TrapezoidHero.module.scss';
import type { TrapezoidHeroProps } from './types';
import { withChildren } from '@builder.io/react';
import { useEffect, useRef } from 'react';
import { cls } from '@/utils/cls';
import RichText from '../RichText';
export function TrapezoidHero({
  extendStyles,
  children,
  heading,
  headingAlignment,
  copy,
  copyAlignment,
  lowerCopy,
  backgroundImage,
  textBackgroundColor,
  topTrapezoidWidth,
  bottomTrapezoidWidth,
  topTrapezoidWidthMobile,
  bottomTrapezoidWidthMobile
}: TrapezoidHeroProps) {
  const _className = cls(styles.trapezoidHero, extendStyles);
  const lowerClassNames = cls(styles.lower);
  const mobileTrapezoidWidth = Math.max(topTrapezoidWidthMobile, bottomTrapezoidWidthMobile);
  const mobileImageWidth = 100 - Math.min(topTrapezoidWidthMobile, bottomTrapezoidWidthMobile);
  const desktopTrapezoidWidth = Math.max(topTrapezoidWidth, bottomTrapezoidWidth);
  const desktopImageWidth = 100 - Math.min(topTrapezoidWidth, bottomTrapezoidWidth);
  const hasButton = !!children && !!children.length;
  const containerRef = useRef<HTMLElement>(null);
  const desktopContentRef = useRef<HTMLDivElement>(null);
  const mobileContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current && (desktopContentRef.current || mobileContentRef.current)) {
        const desktopGrid = desktopContentRef.current?.querySelector('[data-grid]');
        const mobileGrid = mobileContentRef.current?.querySelector('[data-grid]');
        const desktopHeight = desktopGrid ? desktopGrid.scrollHeight : 0;
        const mobileHeight = mobileGrid ? mobileGrid.scrollHeight : 0;
        const maxHeight = Math.max(desktopHeight, mobileHeight);
        containerRef.current.style.height = `${maxHeight + 100}px`;
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [heading, copy, lowerCopy, children]);
  return <section className={_className} ref={containerRef} data-sentry-component="TrapezoidHero" data-sentry-source-file="TrapezoidHero.tsx">
			{backgroundImage?.src && <div data-testid="desktop-background" className={cls(styles.heroBackgroundImage, styles.desktop)} style={{
      backgroundImage: `url(${backgroundImage.src})`,
      width: `${desktopImageWidth}%`
    }}></div>}
			{backgroundImage?.src && <div data-testid="mobile-background" className={cls(styles.heroBackgroundImage, styles.mobile)} style={{
      backgroundImage: `url(${backgroundImage.src})`,
      width: `${mobileImageWidth}%`
    }}></div>}
			<div ref={mobileContentRef} data-testid="mobile-trapezoid" className={cls(styles.mobile, styles.trapezoid)} style={{
      background: textBackgroundColor,
      width: `${mobileTrapezoidWidth}%`,
      clipPath: `polygon(0% 0%, 100% 0%, ${(100 - mobileImageWidth) / mobileTrapezoidWidth * 100}% 100%, 0% 100%)`
    }}>
				<div className={styles.grid} style={{
        width: `${(100 - mobileImageWidth) / mobileTrapezoidWidth * 100}%`
      }} data-grid data-testid="mobile-grid">
					{heading && <RichText text={heading} className={styles.heading} data-align={headingAlignment} />}
					<article className={cls(styles.wrapper)}>
						<div className={styles.middleWrap}>
							{copy && <RichText text={copy} className={styles.copy} data-align={copyAlignment} />}
						</div>

						{lowerCopy && <div className={lowerClassNames}>
								<RichText text={lowerCopy} className={styles.copy} data-align={copyAlignment} />
							</div>}
					</article>
					{hasButton && children}
				</div>
			</div>
			<div ref={desktopContentRef} data-testid="desktop-trapezoid" className={cls(styles.desktop, styles.trapezoid)} style={{
      background: textBackgroundColor,
      width: `${desktopTrapezoidWidth}%`,
      clipPath: `polygon(0% 0%, 100% 0%, ${(100 - desktopImageWidth) / desktopTrapezoidWidth * 100}% 100%, 0% 100%)`
    }}>
				<div className={styles.grid} style={{
        width: `${(100 - desktopImageWidth) / desktopTrapezoidWidth * 100}%`
      }} data-grid data-testid="desktop-grid">
					{heading && <RichText text={heading} className={styles.heading} data-align={headingAlignment} data-testid="heading" />}
					<article className={cls(styles.wrapper)}>
						<div className={styles.middleWrap}>
							{copy && <RichText text={copy} className={styles.copy} data-align={copyAlignment} data-testid="copy" />}
						</div>

						{lowerCopy && <div className={lowerClassNames} data-testid="lower-copy">
								<RichText text={lowerCopy} className={styles.copy} data-align={copyAlignment} />
							</div>}
					</article>
					{hasButton && children}
				</div>
			</div>
		</section>;
}
export default withChildren(TrapezoidHero);